import { Graphics, WebGL } from "./graphics";
import * as shaders from "./shaders";
import { Vec2 } from "../lib/math";

export type Color = [number, number, number, number];

export type LightingStyle = {
  lightColors: [
    // Light of cursor
    Color,

    // Light of layers moving around
    Color,
    Color,
    Color
  ];
  backgroundColor: Color;
  materialColor: Color;
};

const LIGHTING_STYLE_IDS = ["default", "blue", "woodland-elves", "bubblegum"];

type LightingStyleId = typeof LIGHTING_STYLE_IDS[number];

const colorStyles = new Map<LightingStyleId, LightingStyle>();

colorStyles.set("default", {
  lightColors: [
    [0.9921568627450981, 0.8235294117647058, 0.5254901960784314, 1],
    [0.9764705882352941, 0.47058823529411764, 0.47058823529411764, 1],
    [0.7058823529411765, 0.7058823529411765, 1, 1],
    [0.6901960784313725, 0.9098039215686274, 0.6901960784313725, 1],
  ],
  backgroundColor: [
    0.4470588235294118,
    0.30980392156862746,
    0.30980392156862746,
    1,
  ],
  materialColor: [
    0.1843137254901961,
    0.33725490196078434,
    0.41568627450980394,
    1,
  ],
});

colorStyles.set("blue", {
  lightColors: [
    [0.9921568627450981, 0.8235294117647058, 0.5254901960784314, 1],
    [0.19607843137254902, 0.4235294117647059, 0.7215686274509804, 1],
    [0.06666666666666667, 0.38823529411764707, 0.6352941176470588, 1],
    [0.07450980392156863, 0.2549019607843137, 0.5843137254901961, 1],
  ],
  backgroundColor: [
    0.1568627450980392,
    0.13725490196078433,
    0.3215686274509804,
    1,
  ],
  materialColor: [
    0.047058823529411764,
    0.050980392156862744,
    0.11372549019607843,
    1,
  ],
});

colorStyles.set("woodland-elves", {
  lightColors: [
    [0.8745098039215686, 0.8156862745098039, 0.42745098039215684, 1],
    [0.6666666666666666, 0.7647058823529411, 0.17254901960784313, 1],
    [0.47058823529411764, 0.38823529411764707, 0.27058823529411763, 1],
    [0.5607843137254902, 0.4588235294117647, 0.23921568627450981, 1],
  ],
  backgroundColor: [
    0.043137254901960784,
    0.12156862745098039,
    0.0392156862745098,
    1,
  ],
  materialColor: [
    0.09411764705882353,
    0.16862745098039217,
    0.03137254901960784,
    1,
  ],
});

colorStyles.set("bubblegum", {
  lightColors: [
    [1, 0.9607843137254902, 0.7019607843137254, 1],
    [1, 0.796078431372549, 0.23921568627450981, 1],
    [0.9686274509803922, 0.7647058823529411, 0.996078431372549, 1],
    [1, 0.8588235294117647, 0.8862745098039215, 1],
  ],
  backgroundColor: [
    0.5529411764705883,
    0.803921568627451,
    0.8862745098039215,
    1,
  ],
  materialColor: [
    0.6980392156862745,
    0.8823529411764706,
    0.9607843137254902,
    1,
  ],
});

export type RenderMode =
  | "INPUT_CANVAS"
  | "RAW_DISTANCE_FIELD"
  | "DISTANCE_FIELD"
  | "HARD_SHADOWS"
  | "SOFT_SHADOWS_1"
  | "SOFT_SHADOWS_2"
  | "ALL";

export class Renderer {
  private _canvas: HTMLCanvasElement;
  private _lightPosition: Vec2 = new Vec2(100, 200);
  private _style: LightingStyle;

  // Graphics resources
  private _gl: WebGL.Context;
  private _quadBuffer: Graphics.VertexBuffer;

  private _tempTexture: Graphics.Texture;
  private _overlayTexture: Graphics.Texture | null;
  private _distanceField: Graphics.Texture;

  private _drawTextureProgram: Graphics.Material;
  private _distanceFieldProgram: Graphics.Material;
  private _hardShadowsProgram: Graphics.Material;
  private _softShadows1Program: Graphics.Material;
  private _softShadows2Program: Graphics.Material;
  private _allProgram: Graphics.Material;

  constructor(
    canvas: HTMLCanvasElement,
    private inputCanvas: HTMLCanvasElement
  ) {
    this._canvas = canvas;

    this._gl = new WebGL.Context(canvas);

    const gl = this._gl;
    gl.setCopyBlendState();

    const vertexFormat = new Graphics.VertexFormat();
    vertexFormat.add(shaders.GLSLX_NAME_QUAD, Graphics.AttributeType.FLOAT, 2);

    this._allProgram = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_FRAG
    );

    this._drawTextureProgram = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_DRAW_TEXTURE_F
    );

    this._distanceFieldProgram = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_DRAW_DISTANCE_FIELD_F
    );
    this._hardShadowsProgram = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_DRAW_HARD_SHADOWS_F
    );
    this._softShadows1Program = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_DRAW_SOFT_SHADOWS1_F
    );
    this._softShadows2Program = gl.createMaterial(
      vertexFormat,
      shaders.GLSLX_SOURCE_VERT,
      shaders.GLSLX_SOURCE_DRAW_SOFT_SHADOWS2_F
    );

    this._distanceField = gl.createTexture(
      Graphics.TextureFormat.NEAREST_CLAMP,
      1,
      1
    );
    this._tempTexture = gl.createTexture(
      Graphics.TextureFormat.NEAREST_CLAMP,
      1,
      1
    );

    const QUAD = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    this._quadBuffer = gl.createVertexBuffer(vertexFormat.stride * QUAD.length);
    this._quadBuffer.upload(new Uint8Array(QUAD.buffer));

    this._style = colorStyles.get("blue")!;
    // (localStorage["style"] != null
    //   ? JSON.parse(localStorage["style"])
    //   : null) || colorStyles.get("default");
  }

  setOverlay(overlay: HTMLCanvasElement | null) {
    if (overlay != null) {
      if (this._overlayTexture == null) {
        const gl = this._gl;
        this._overlayTexture = gl.createTexture(
          Graphics.TextureFormat.NEAREST_CLAMP,
          1,
          1
        );
      }

      this._overlayTexture.resize(overlay.width, overlay.height, overlay);
    } else {
      this._overlayTexture = null;
    }
  }

  lightingStyle(): LightingStyle {
    return this._style;
  }

  setLightingStyle(style: LightingStyle) {
    this._style = style;
    localStorage["style"] = JSON.stringify(style);
  }

  setDistanceField(canvas: HTMLCanvasElement) {
    this._distanceField.resize(canvas.width, canvas.height, canvas);
  }

  setLightPosition(position: Vec2) {
    this._lightPosition = position;
  }

  handleWindowResize = () => {
    const width = this._canvas.width;
    const height = this._canvas.height;
    this._gl.resize(width, height, width, height);
  };

  readPixel(x: number, y: number): [number, number, number, number] {
    const gl = this._gl.gl;
    const pixels = new Uint8Array(4);
    gl.readPixels(
      x,
      this._canvas.height - y,
      1,
      1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels
    );
    const [r, g, b, a] = pixels;
    return [r, g, b, a];
  }

  setLightingUniforms(material: Graphics.Material) {
    material.setUniformFloat(
      shaders.GLSLX_NAME_U_LIGHT_RADIUS,
      (Math.max(window.innerWidth, window.innerHeight) *
        window.devicePixelRatio) /
        2
    );
    material.setUniformFloat(
      shaders.GLSLX_NAME_U_DEVICE_PIXEL_RATIO,
      window.devicePixelRatio
    );
    material.setUniformVec4(
      shaders.GLSLX_NAME_U_LIGHT_COLOR0,
      this._style.lightColors[0][0],
      this._style.lightColors[0][1],
      this._style.lightColors[0][2],
      this._style.lightColors[0][3]
    );

    material.setUniformVec4(
      shaders.GLSLX_NAME_U_LIGHT_COLOR1,
      this._style.lightColors[1][0],
      this._style.lightColors[1][1],
      this._style.lightColors[1][2],
      this._style.lightColors[1][3]
    );

    material.setUniformVec4(
      shaders.GLSLX_NAME_U_LIGHT_COLOR2,
      this._style.lightColors[2][0],
      this._style.lightColors[2][1],
      this._style.lightColors[2][2],
      this._style.lightColors[2][3]
    );

    material.setUniformVec4(
      shaders.GLSLX_NAME_U_BACKGROUND_COLOR,
      this._style.backgroundColor[0],
      this._style.backgroundColor[1],
      this._style.backgroundColor[2],
      this._style.backgroundColor[3]
    );

    material.setUniformVec4(
      shaders.GLSLX_NAME_U_MATERIAL_COLOR,
      this._style.materialColor[0],
      this._style.materialColor[1],
      this._style.materialColor[2],
      this._style.materialColor[3]
    );
  }

  setTimeResolutionAndLightPositionUniforms(material: Graphics.Material) {
    material.setUniformVec2(
      shaders.GLSLX_NAME_U_RESOLUTION,
      this._canvas.width,
      this._canvas.height
    );
    material.setUniformVec2(
      shaders.GLSLX_NAME_U_LIGHT_POSITION,
      this._lightPosition.x,
      this._lightPosition.y
    );
    material.setUniformFloat(
      shaders.GLSLX_NAME_U_TIME_MS,
      window.performance.now()
    );
  }

  drawAll() {
    const gl = this._gl;
    const material = this._allProgram;
    material.setUniformSampler(
      shaders.GLSLX_NAME_U_TEXTURE,
      this._distanceField,
      0
    );

    this.setLightingUniforms(material);
    this.setTimeResolutionAndLightPositionUniforms(material);

    gl.setRenderTarget(null);
    gl.setViewport(0, 0, this._canvas.width, this._canvas.height);
    gl.draw(Graphics.Primitive.TRIANGLE_STRIP, material, this._quadBuffer);
  }

  drawInputCanvas() {
    this._tempTexture.resize(
      this.inputCanvas.width,
      this.inputCanvas.height,
      this.inputCanvas
    );
    this.drawTexture(this._tempTexture);
  }

  drawRawDistanceField() {
    this.drawTexture(this._distanceField);
  }

  drawTexture(texture: Graphics.Texture) {
    const gl = this._gl;
    const material = this._drawTextureProgram;

    material.setUniformSampler(shaders.GLSLX_NAME_U_TEXTURE, texture, 0);

    gl.setRenderTarget(null);
    gl.setViewport(0, 0, this._canvas.width, this._canvas.height);
    gl.draw(Graphics.Primitive.TRIANGLE_STRIP, material, this._quadBuffer);
  }

  drawDistanceField() {
    const gl = this._gl;
    const material = this._distanceFieldProgram;

    material.setUniformSampler(
      shaders.GLSLX_NAME_U_TEXTURE,
      this._distanceField,
      0
    );
    material.setUniformVec2(
      shaders.GLSLX_NAME_U_RESOLUTION,
      this._canvas.width,
      this._canvas.height
    );

    gl.setRenderTarget(null);
    gl.setViewport(0, 0, this._canvas.width, this._canvas.height);
    gl.draw(Graphics.Primitive.TRIANGLE_STRIP, material, this._quadBuffer);
  }

  drawHardShadows() {
    const material = this._hardShadowsProgram;
    this.drawShadows(material);
  }

  drawSoftShadows1() {
    const material = this._softShadows1Program;
    this.drawShadows(material);
  }

  drawSoftShadows2() {
    const material = this._softShadows2Program;
    this.drawShadows(material);
  }

  drawShadows(material: Graphics.Material) {
    const gl = this._gl;
    material.setUniformSampler(
      shaders.GLSLX_NAME_U_TEXTURE,
      this._distanceField,
      0
    );

    material.setUniformVec2(
      shaders.GLSLX_NAME_U_RESOLUTION,
      this._canvas.width,
      this._canvas.height
    );
    material.setUniformVec2(
      shaders.GLSLX_NAME_U_LIGHT_POSITION,
      this._lightPosition.x,
      this._lightPosition.y
    );

    gl.setRenderTarget(null);
    gl.setViewport(0, 0, this._canvas.width, this._canvas.height);
    gl.draw(Graphics.Primitive.TRIANGLE_STRIP, material, this._quadBuffer);
  }

  draw(mode: RenderMode) {
    const gl = this._gl;
    gl.setCopyBlendState();

    switch (mode) {
      case "INPUT_CANVAS": {
        this.drawInputCanvas();
        break;
      }
      case "RAW_DISTANCE_FIELD": {
        this.drawRawDistanceField();
        break;
      }
      case "DISTANCE_FIELD": {
        this.drawDistanceField();
        break;
      }
      case "HARD_SHADOWS": {
        this.drawHardShadows();
        break;
      }
      case "SOFT_SHADOWS_1": {
        this.drawSoftShadows1();
        break;
      }
      case "SOFT_SHADOWS_2": {
        this.drawSoftShadows2();
        break;
      }
      case "ALL": {
        this.drawAll();
        break;
      }
    }

    if (this._overlayTexture != null) {
      gl.setPremultipliedBlendState();
      this.drawTexture(this._overlayTexture);
    }
  }
}
